import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzCardModule } from 'ng-zorro-antd/card';
import { NzInputModule } from 'ng-zorro-antd/input';
import { NzMessageService } from 'ng-zorro-antd/message';
import { NzTableModule } from 'ng-zorro-antd/table';
import { NzTagModule } from 'ng-zorro-antd/tag';
import { AdminSeatsService } from '../services/admin-seats.service';
import { shouldSuppressGenericError } from '../utils/http-error.utils';

interface EndpointValidationErrors {
  hostname?: string;
  port?: string;
  hostKeyFingerprint?: string;
}

interface ReadinessValidationErrors {
  sftpUsername?: string;
  uploadDirectory?: string;
  acceptedFileExtension?: string;
  sftpGoProvisioningConfirmed?: string;
}

@Component({
  selector: 'app-admin-integrations',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    TranslateModule,
    NzCardModule,
    NzInputModule,
    NzButtonModule,
    NzTableModule,
    NzTagModule,
  ],
  templateUrl: './admin-integrations.component.html',
  styleUrl: './admin-integrations.component.less',
})
export class AdminIntegrationsComponent implements OnInit {
  companies: any[] = [];
  sftpRequests: any[] = [];
  sftpEndpoint: any = null;
  sftpEndpointMeta: any = null;
  sftpSetupAvailable = false;
  selectedSftpRequest: any = null;
  readiness: any = { sftpUsername: '', uploadDirectory: '/', s3Prefix: '', acceptedFileExtension: 'xlsx', sftpGoProvisioningConfirmed: false };
  endpointErrors: EndpointValidationErrors = {};
  readinessErrors: ReadinessValidationErrors = {};
  loading = false;
  search = '';

  constructor(
    private adminSeatsService: AdminSeatsService,
    private message: NzMessageService,
    private translate: TranslateService
  ) {}

  ngOnInit(): void {
    this.load();
    this.loadSftp();
  }

  loadSftp(): void {
    this.adminSeatsService.getSftpEndpoint().subscribe({
      next: (response) => {
        this.sftpEndpoint = response.endpoint || {};
        this.sftpEndpointMeta = response;
        this.sftpSetupAvailable = true;
        this.adminSeatsService.getSftpRequests().subscribe({
          next: (requestsResponse) => (this.sftpRequests = requestsResponse.items || []),
          error: () => (this.sftpRequests = []),
        });
      },
      error: () => {
        this.sftpEndpoint = null;
        this.sftpSetupAvailable = false;
        this.sftpRequests = [];
      }
    });
  }

  saveSftpEndpoint(): void {
    this.endpointErrors = this.validateEndpoint();
    if (Object.keys(this.endpointErrors).length) return;
    this.adminSeatsService.updateSftpEndpoint(this.sftpEndpoint).subscribe({
      next: () => {
        this.message.success(this.translate.instant('ADMIN.INTEGRATIONS.SFTP_SAVED'));
        this.loadSftp();
      },
      error: (error) => this.showSftpError(error, 'ADMIN.INTEGRATIONS.SFTP_SAVE_ERROR')
    });
  }

  selectSftpRequest(request: any): void {
    this.adminSeatsService.getSftpRequest(request.connectionId).subscribe({
      next: (response) => {
        this.selectedSftpRequest = { ...response.item, company: request.company || null };
        this.readinessErrors = {};
        this.readiness = {
          sftpUsername: response.item.endpoint?.username || response.item.sftpUsername || '',
          uploadDirectory: response.item.endpoint?.uploadDirectory || response.item.uploadDirectory || '/',
          s3Prefix: response.item.expectedS3Prefix || response.item.s3Prefix || '',
          acceptedFileExtension: response.item.acceptedFileExtension || 'xlsx',
          sftpGoProvisioningConfirmed: false,
        };
      },
      error: (error) => this.showSftpError(error, 'ADMIN.INTEGRATIONS.SFTP_LOAD_ERROR')
    });
  }

  markSftpReady(): void {
    if (!this.selectedSftpRequest?.id) return;
    this.readinessErrors = this.validateReadiness();
    if (Object.keys(this.readinessErrors).length) return;
    this.adminSeatsService.markSftpReady(this.selectedSftpRequest.id, this.readiness).subscribe({
      next: () => {
        this.message.success(this.translate.instant('ADMIN.INTEGRATIONS.SFTP_READY'));
        this.selectedSftpRequest = null;
        this.loadSftp();
      },
      error: (error) => this.showSftpError(error, 'ADMIN.INTEGRATIONS.SFTP_READY_ERROR')
    });
  }

  changeSftpState(request: any, action: 'pause' | 'resume' | 'disconnect' | 'error'): void {
    this.adminSeatsService.changeSftpState(request.connectionId, action).subscribe({
      next: () => this.loadSftp(),
      error: (error) => this.showSftpError(error, 'ADMIN.INTEGRATIONS.SFTP_STATE_ERROR'),
    });
  }

  load(): void {
    this.loading = true;
    this.adminSeatsService.getCompanies().subscribe({
      next: (companies) => {
        this.companies = companies || [];
        this.loading = false;
      },
      error: () => {
        this.companies = [];
        this.loading = false;
      }
    });
  }

  saveCompany(company: any): void {
    this.adminSeatsService
      .updateCompanyIntegrations(company._id, { integrationLimit: Math.max(0, Number(company.integrationLimit || 0)) })
      .subscribe({
        next: () => {
          this.message.success(this.translate.instant('ADMIN.SEATS_RBAC.SAVED'));
          this.load();
        },
        error: () => {
          this.message.error(this.translate.instant('ERRORS.NOT_AUTHORIZED'));
        }
      });
  }

  get filteredCompanies(): any[] {
    const term = this.search.trim().toLowerCase();
    if (!term) {
      return this.companies;
    }
    return this.companies.filter((company) => `${company.name || ''} ${company.domain || ''}`.toLowerCase().includes(term));
  }

  canProvision(request: any): boolean {
    return ['awaiting_provisioning', 'disconnected', 'error'].includes(request?.lifecycleStatus);
  }

  lifecycleLabel(status: string): string {
    return this.translate.instant(`INTEGRATION.SFTP.LIFECYCLE_LABELS.${status || 'error'}`);
  }

  private validateEndpoint(): EndpointValidationErrors {
    const errors: EndpointValidationErrors = {};
    const hostname = String(this.sftpEndpoint?.hostname || '').trim();
    const port = Number(this.sftpEndpoint?.port);
    const fingerprint = String(this.sftpEndpoint?.hostKeyFingerprint || '').trim();
    if (!/^(?=.{1,253}$)(?:[A-Za-z0-9](?:[A-Za-z0-9-]{0,61}[A-Za-z0-9])?\.)+[A-Za-z]{2,63}$/.test(hostname)) {
      errors.hostname = this.translate.instant('ADMIN.INTEGRATIONS.SFTP_HOST_INVALID');
    }
    if (!Number.isInteger(port) || port < 1 || port > 65535) {
      errors.port = this.translate.instant('ADMIN.INTEGRATIONS.SFTP_PORT_INVALID');
    }
    if (this.sftpEndpoint?.provisioningEnabled && !/^SHA256:[A-Za-z0-9+/]{20,}={0,2}$/.test(fingerprint)) {
      errors.hostKeyFingerprint = this.translate.instant('ADMIN.INTEGRATIONS.SFTP_FINGERPRINT_INVALID');
    }
    return errors;
  }

  private validateReadiness(): ReadinessValidationErrors {
    const errors: ReadinessValidationErrors = {};
    const username = String(this.readiness?.sftpUsername || '').trim();
    const directory = String(this.readiness?.uploadDirectory || '').trim();
    if (!username || username.length > 160 || !/^[A-Za-z0-9._-]+$/.test(username)) {
      errors.sftpUsername = this.translate.instant('ADMIN.INTEGRATIONS.SFTP_USERNAME_INVALID');
    }
    if (!directory || !directory.startsWith('/') || directory.includes('..') || directory.length > 512) {
      errors.uploadDirectory = this.translate.instant('ADMIN.INTEGRATIONS.SFTP_UPLOAD_DIRECTORY_INVALID');
    }
    if (!['xlsx', 'xls'].includes(this.readiness?.acceptedFileExtension)) {
      errors.acceptedFileExtension = this.translate.instant('ADMIN.INTEGRATIONS.SFTP_FORMAT_INVALID');
    }
    if (this.readiness?.sftpGoProvisioningConfirmed !== true) {
      errors.sftpGoProvisioningConfirmed = this.translate.instant('ADMIN.INTEGRATIONS.SFTP_CONFIRM_REQUIRED');
    }
    return errors;
  }

  private errorMessage(error: { error?: { message?: string }, message?: string }, fallbackKey: string): string {
    return error?.error?.message || error?.message || this.translate.instant(fallbackKey);
  }

  private showSftpError(error: unknown, fallbackKey: string): void {
    if (shouldSuppressGenericError(error)) return;
    this.message.error(this.errorMessage(error as { error?: { message?: string }, message?: string }, fallbackKey));
  }
}

